<script setup>
import { ref, computed, onMounted, nextTick, getCurrentInstance } from 'vue';

const props = defineProps({
    options: Object,
});

const emit = defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const router = proxy.$router;

// --- State ---

const modal = ref(null);
const codeMirrorTextArea = ref(null);
const cmInstance = ref(null);

const items = ref([]);
const searchTimeout = ref(undefined);
const value = ref(undefined);
const selections = ref([]);

// --- Computed ---

const searchStr = computed({
    get() {
        return store.state.themes.searchStr;
    },
    set(newValue) {
        // Direct mutation as per original component logic
        store.state.themes.searchStr = newValue;
    }
});

const findModalItemIndex = computed(() => {
    return store.state.themes.findModalItemIndex;
});

// --- Methods ---

const search = () => {
    if (searchStr.value === '') {
        items.value = false;
        store.state.themes.findModalItemIndex = 0;
        value.value = undefined;
    } else {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value);
        }
        searchTimeout.value = setTimeout(() => {
            setItems().then(() => {
                if (items.value && items.value.length) {
                    selectItem(findModalItemIndex.value);
                }
            });
        }, 500);
    }
};

const setItems = () => {
    return store.dispatch('request/get', {
            url: 'api/themes-find',
            params: {
                search: searchStr.value
            }
        })
        .then((obj) => {
            items.value = obj.data.items;
        });
};

const selectItem = (i) => {
    store.state.themes.findModalItemIndex = i;
    value.value = false;

    let item = items.value[i];
    store.dispatch('itemData/get', {
            tableName: item.tableName,
            id: item.id
        })
        .then((obj) => {
            let code;
            let mode;
            switch (item.tableName) {
                case 'templates_templates':
                    code = obj.templateHtml;
                    mode = 'text/html';
                    break;
                case 'themes_css':
                    code = obj.css;
                    mode = 'css';
                    break;
                case 'themes_javascript':
                    code = obj.script;
                    mode = 'javascript';
                    break;
            }
            showCodeMirror(code, mode, item.lineNumber, item.positions);
        });
};

const showCodeMirror = (code, mode, lineNumber, positions) => {
    value.value = code;

    let codeMirrorOptions = {
        mode: mode,
        styleActiveLine: {
            nonEmpty: true
        },
        lineNumbers: true,
        lineWrapping: false,
        readOnly: true
    };

    let height = getCodeMirrorHeight();
    const codeMirrorPrefs = store.getters['user/pref']('codeMirror') || {};

    if (codeMirrorPrefs.theme) {
        codeMirrorOptions['theme'] = codeMirrorPrefs.theme;
    }

    nextTick(() => {
        // eslint-disable-next-line no-undef
        cmInstance.value = CodeMirror.fromTextArea(codeMirrorTextArea.value, codeMirrorOptions);
        cmInstance.value.setSize(null, height);

        setSelections(positions, lineNumber);
        cmInstance.value.setSelections(selections.value);

        // Ensure rendering is correct after transition
        setTimeout(() => {
            cmInstance.value.refresh();
        }, 100);
    });
};

const setSelections = (positions, lineNumber) => {
    selections.value = [];
    let len = searchStr.value.length;

    positions.forEach((position) => {
        selections.value.push({
            anchor: {
                line: lineNumber,
                ch: position
            },
            head: {
                line: lineNumber,
                ch: position + len
            }
        });
    });
};

const openItem = (i) => {
    store.commit('themes/findModalSelections', selections.value);

    let item = items.value[i];
    router.push({path: '/themes_editor', query: {section: item.tableName, id: item.id}});
    emit('hide-modal');
};

const getCodeMirrorHeight = () => {
    let el = modal.value;
    if (!el) return '300px';

    let bottom = el.getBoundingClientRect().bottom;
    let marginBottom = parseInt(window.getComputedStyle(el).marginBottom, 10);
    let modalHeight = bottom + marginBottom;

    return (window.innerHeight - modalHeight) + 'px';
};

// --- Lifecycle ---

onMounted(() => {
    if (searchStr.value) {
        search();
    }
});
</script>

<template>
    <div ref="modal" class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Find</h4>

                <button aria-label="Close" class="btn-close" type="button" @click="$emit('hide-modal')"></button>
            </div>

            <div class="modal-body">
                <input v-model="searchStr" v-select autocomplete="off" class="form-control input-lg" placeholder="Search"
                       type="text" @input="search">

                <div v-if="items" class="my-3 list-group list-group-numbered list-group-flush">
                    <div v-for="(item, index) in items" :key="index" :class="{ 'active': index === findModalItemIndex }"
                         class="list-group-item d-flex py-1 px-3" @click="selectItem(index)"
                         @dblclick="openItem(index)">
                        <div class="flex-fill ps-1 text-nowrap overflow-hidden" v-html="item.line"></div>

                        <div class="flex-shrink-1 text-nowrap text-end ps-3">
                            {{ item.title }} {{ item.lineNumber + 1 }}
                        </div>
                    </div>
                </div>

                <div v-if="value">
                    <textarea ref="codeMirrorTextArea" v-html="value"/>
                </div>
            </div>

            <div class="modal-footer"></div>
        </div>
    </div>
</template>

<style scoped>
.list-group {
    max-height: 150px;
    overflow-y: auto;
    border: 1px solid #ccc;
    border-radius: 5px;
}

.list-group-item:first-child,
.list-group-item:last-child {
    border-radius: 0 !important;
}
</style>